import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    type InputSignal,
    type OutputEmitterRef,
    type Signal,
    type WritableSignal,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { type ICountryPhoneMetaDto } from '@pecan/api';
import { LocationState } from '../../states/location.state';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { Store } from '@ngxs/store';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatSelect, MatOption],
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CountrySelectComponent),
        },
    ],
    selector: 'app-country-select',
    templateUrl: './country-select.component.html',
})
export class CountrySelectComponent implements ControlValueAccessor {
    public readonly countryChange: OutputEmitterRef<ICountryPhoneMetaDto | null> = output<ICountryPhoneMetaDto | null>();

    public readonly required: InputSignal<boolean> = input<boolean>(true);

    public readonly countries: Signal<ICountryPhoneMetaDto[] | null>;

    public readonly value: WritableSignal<string> = signal<string>('');

    public readonly disabled: WritableSignal<boolean> = signal<boolean>(false);

    private readonly _store: Store = inject(Store);

    constructor() {
        this.countries = this._store.selectSignal(LocationState.countries);
    }

    public writeValue(value: string): void {
        this.value.set(value);

        this._emitCountryChange(value);
    }

    public registerOnChange(fn: (value: string) => void): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }

    public onSelectionChange(value: string): void {
        this.value.set(value);
        this._onChange(value);
        this._onTouched();

        this._emitCountryChange(value);
    }

    public trackByCountry(_index: number, country: ICountryPhoneMetaDto): string {
        return country.isoCode;
    }

    private _onChange: (value: string) => void = (): void => {
        // Empty implementation
    };

    private _onTouched: () => void = (): void => {
        // Empty implementation
    };

    private _emitCountryChange(value: string): void {
        const countries = this.countries();

        if (!countries) {
            return;
        }

        const selectedCountry = countries.find((c: ICountryPhoneMetaDto) => c.name === value);

        this.countryChange.emit(selectedCountry ?? null);
    }
}
